import { useEffect, useState, useRef } from 'react';
import useVideoRoom from '../video/utils/useVideoRoom';
import { State } from '../video/types/Video';
import { InCallUpgrade } from '../video/inCallUpgrade/inCallUpgrade';
import { PostCall } from '../video/postCall/PostCall';
import Loading from '../video/loading/Loading';
import { useLocation } from 'react-router-dom';
import { logger } from 'utils/logger';
import { createVideoSessionToken } from '../video/utils/createVideoSessionToken';
import { PermissionWarning } from '../video/shared/PermissionWarning';

import { poweredByLink } from '../../components/video/shared/variables';


import {
    SettingsModalTitle,
    RotateCameraButton,
    SettingsButton,
    SettingsModal,
    InCallWrapper,
    ToggleButton,
    DialPadModal,
    RemoteVideo,
    BlurButton,
    LocalVideo,
    Disconnect,
    PoweredBy,
    Controls,
    Content,
    Middle,
    Center,
    Right,
    Media,
    Left,
} from '../video/inCallUpgradeVonage/InCallUpgradeVonageStyle';

import { ReactComponent as MicrophoneNotFoundSvg } from 'assets/icons/microphoneNotFound.svg';
import { ReactComponent as MicrophoneMutedSvg } from 'assets/icons/microphoneMuted.svg';
import { ReactComponent as CameraNotFound } from 'assets/icons/cameraNotFound.svg';
import { ReactComponent as RotateCameraSvg } from 'assets/icons/arrowRepeat.svg';
import { ReactComponent as CameraMutedSvg } from 'assets/icons/cameraMuted.svg';
import { ReactComponent as MicrophoneSvg } from 'assets/icons/microhpone.svg';
import { ReactComponent as SettingsSvg } from 'assets/icons/settings.svg';
import { ReactComponent as DialPadSvg } from 'assets/icons/dialPad.svg';
import { ReactComponent as PersonSvg } from 'assets/icons/person.svg';
import { ReactComponent as CameraSvg } from 'assets/icons/camera.svg';
import { ReactComponent as PhoneSvg } from 'assets/icons/phone.svg';
import { ReactComponent as BlurSvg } from 'assets/icons/blur.svg';

import OT from '@vonage/client-sdk-video';

type MainCameraIds = { user: string | null; environment: string | null };

// Every line from this flow is prefixed so it can be filtered in New Relic
// (console.* is forwarded there by utils/consoleForwarder).
const TAG = '[VideoUpgradeVonage]';
const log = {
    info: (...args: any[]) => console.log(TAG, ...args),
    warn: (...args: any[]) => console.warn(TAG, ...args),
    error: (...args: any[]) => console.error(TAG, ...args),
};

// Attached by New Relic to every log/event of this page load, so a single
// client's call can be found by session, account or interaction id.
const setNrAttribute = (name: string, value: string | null) => {
    try {
        (window as any).NrAgent?.setCustomAttribute(name, value);
    } catch {
        // Missing or blocked agent must not break the call.
    }
};

// SDK objects (streams, events) are huge and circular, so only the fields
// that matter for debugging are logged.
const describeStream = (stream: any) => stream && ({
    streamId: stream.streamId,
    connectionId: stream.connection?.connectionId,
    hasAudio: stream.hasAudio,
    hasVideo: stream.hasVideo,
    videoType: stream.videoType,
    videoDimensions: stream.videoDimensions,
});

const describeTrack = (track?: MediaStreamTrack | null) => track && ({
    kind: track.kind,
    label: track.label,
    readyState: track.readyState,
    enabled: track.enabled,
    muted: track.muted,
});

// A live track stops delivering media without any error when the OS takes the
// device away (e.g. iOS during a phone call) - only mute/unmute events show it.
const watchedTracks = new WeakSet<MediaStreamTrack>();

const watchTrack = (track?: MediaStreamTrack | null) => {
    if (!track || watchedTracks.has(track)) return;
    watchedTracks.add(track);

    log.info('Watching track:', describeTrack(track));
    track.addEventListener('mute', () => log.warn('Track muted (OS stopped delivering media):', describeTrack(track)));
    track.addEventListener('unmute', () => log.info('Track unmuted (media flowing again):', describeTrack(track)));
    track.addEventListener('ended', () => log.warn('Track ended:', describeTrack(track)));
}

const describePublisherSources = (publisher: any) => {
    try {
        const video = publisher?.getVideoSource?.();
        return {
            video: video && { deviceId: video.deviceId, type: video.type, track: describeTrack(video.track) },
            audio: describeTrack(publisher?.getAudioSource?.()),
        };
    } catch (error) {
        return { error: String(error) };
    }
};

// Snapshot of everything that decides whether getUserMedia can succeed.
const logEnvironment = async () => {
    const permissions: Record<string, string> = {};
    for (const name of ['camera', 'microphone']) {
        try {
            const status = await navigator.permissions.query({ name } as unknown as PermissionDescriptor);
            permissions[name] = status.state;
        } catch {
            permissions[name] = 'unsupported';
        }
    }

    let devices: any = 'unavailable';
    try {
        // Labels stay empty until the page has been granted access at least once.
        const list = await navigator.mediaDevices.enumerateDevices();
        devices = list.map((device) => ({ kind: device.kind, label: device.label || '(no label)' }));
    } catch (error) {
        devices = `enumerateDevices failed: ${String(error)}`;
    }

    let inIframe: boolean | string;
    try {
        inIframe = window.self !== window.top;
    } catch {
        inIframe = 'cross-origin';
    }

    log.info('Environment:', {
        secureContext: window.isSecureContext,
        inIframe,
        getUserMediaAvailable: !!navigator.mediaDevices?.getUserMedia,
        otSystemRequirements: OT.checkSystemRequirements(),
        visibility: document.visibilityState,
        permissions,
        devices,
    });
};

const VideoUpgradeVonage = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const interactionId = searchParams.get('interactionId');
    const qSessionId = searchParams.get('sessionId');
    const qAccountId = searchParams.get('accountId');
    const cameraParam = searchParams.get('camera');
    const shouldUseBackCamera = cameraParam === '1';
    const audioParam = searchParams.get('audio');
    const isAudioEnabled = audioParam !== 'false';
    const [currentState, setCurrentState] = useState<State>('preUpgrade');
    const [publisher, setPublisher] = useState<any>(null);
    const [session, setSession] = useState<any>(null);
    const [tokenData, setTokenData] = useState<any>(null);
    const [remoteStream, setRemoteStream] = useState<any>(null);
    const localVideoRef = useRef<HTMLDivElement>(null);
    const [microphoneMuted, setMicrophoneMuted] = useState<boolean>(true);
    const [cameraMuted, setCameraMuted] = useState<boolean>(false);
    const [isVideoBlurred, setIsVideoBlurred] = useState<boolean>(false);
    const [isCameraRotated, setIsCameraRotated] = useState<boolean>(false);
    const mainCameraIdsRef = useRef<MainCameraIds | null>(null);
    const switchingCameraRef = useRef<boolean>(false);
    const [cameraPermissionDenied, setCameraPermissionDenied] = useState<boolean>(false);
    const blurSettings = {
        type: 'backgroundBlur',
        blurStrength: 'high',
    }


    const changeCurrentState = (state: State) => {
        log.info('State change requested by child:', state);
        setCurrentState(state);
    };

    const initVonage = async (sessionId: any, token: any) => {
        const applicationId = process.env.REACT_APP_VONAGE_APPLICATION_ID || "5ca20ba4-561c-42c5-b063-136f6ec614c6";
        log.info('Initializing Vonage:', { applicationId, sessionId, shouldUseBackCamera, isAudioEnabled, microphoneMuted });

        const session = OT.initSession(applicationId, sessionId);

        // Session event listeners
        session.on('streamCreated', (event: any) => {
            const isRemote = !!session.connection && event.stream.connection.connectionId !== session.connection.connectionId;
            log.info('Session streamCreated:', { remote: isRemote, stream: describeStream(event.stream) });
            if (isRemote) {
                // This is the remote stream
                setRemoteStream(event.stream);
                log.info('Subscribing to remote stream:', event.stream.streamId);
                const subscriber = session.subscribe(event.stream, 'remoteVideoInCall', {
                    insertMode: 'append',
                    width: '100%',
                    height: '100%',
                    showControls: false
                }, (error: any) => {
                    if (error) {
                        log.error('Error subscribing to stream:', error);
                    } else {
                        log.info('Successfully subscribed to remote stream:', event.stream.streamId);
                    }
                });

                subscriber.on({
                    connected: () => log.info('Subscriber connected'),
                    disconnected: () => log.warn('Subscriber disconnected (network), SDK is reconnecting'),
                    destroyed: (e: any) => log.info('Subscriber destroyed:', { reason: e.reason }),
                    videoDisabled: (e: any) => log.warn('Subscriber video disabled:', { reason: e.reason }),
                    videoEnabled: (e: any) => log.info('Subscriber video enabled:', { reason: e.reason }),
                    videoDisableWarning: () => log.warn('Subscriber video about to be disabled due to poor network'),
                    videoDisableWarningLifted: () => log.info('Subscriber video disable warning lifted'),
                    audioBlocked: () => log.warn('Subscriber audio blocked by browser autoplay policy'),
                    audioUnblocked: () => log.info('Subscriber audio unblocked'),
                });
            }
        });

        session.on('streamDestroyed', (event: any) => {
            log.info('Session streamDestroyed:', { reason: event.reason, stream: describeStream(event.stream) });
            setRemoteStream(null);
        });

        session.on('streamPropertyChanged', (event: any) => {
            log.info('Session streamPropertyChanged:', {
                streamId: event.stream?.streamId,
                changedProperty: event.changedProperty,
                oldValue: event.oldValue,
                newValue: event.newValue,
            });
        });

        session.on('connectionCreated', (event: any) => {
            log.info('Session connectionCreated:', event.connection?.connectionId);
        });

        session.on('connectionDestroyed', (event: any) => {
            log.info('Session connectionDestroyed:', { connectionId: event.connection?.connectionId, reason: event.reason });
        });

        session.on('sessionConnected', () => {
            log.info('Session connected:', { connectionId: session.connection?.connectionId });
            // Publish the local stream
            log.info('Publishing local stream:', { publisherAccessAllowed: publisher.accessAllowed });
            session.publish(publisher, (error: any) => {
                if (error) {
                    log.error('Error publishing:', error);
                } else {
                    log.info('Successfully published:', {
                        stream: describeStream(publisher.stream),
                        sources: describePublisherSources(publisher),
                    });
                }
            });
        });

        session.on('sessionReconnecting', () => {
            log.warn('Session reconnecting (network interruption)');
        });

        session.on('sessionReconnected', () => {
            log.info('Session reconnected');
        });

        session.on('sessionDisconnected', (event: any) => {
            log.info('Session disconnected:', { reason: event.reason });
        });

        const option: any = {
            height: '100%',
            width: '100%',
            showControls: false,
            publishAudio: !microphoneMuted,
            publishVideo: true,
        }

        // With ?audio=false the microphone is not captured at all - e.g. when the voice
        // already goes over the phone call being upgraded, capturing it as well makes
        // iOS interrupt the whole capture, which mutes the camera track too.
        if (!isAudioEnabled) {
            option.audioSource = null;
        }

        // Let the SDK pick the camera itself - probing for a deviceId with our own
        // getUserMedia call opens a second capture stream and makes the publisher's
        // stream fail with NotReadableError / NotAllowedError.
        if (shouldUseBackCamera) {
            option.facingMode = 'environment';
        }

        log.info('Initializing publisher:', option);
        const publisher = OT.initPublisher('localVideoInCall', option, (error: any) => {
            if (error) {
                if (error.name === 'OT_USER_MEDIA_ACCESS_DENIED') {
                    setCameraPermissionDenied(true);
                }
                log.error('Error initializing publisher:', error);
                // Re-check permissions to tell a user "Block" apart from an OS/browser level denial.
                logEnvironment();
            } else {
                setCameraPermissionDenied(false);
                log.info('Publisher initialized successfully:', describePublisherSources(publisher));
                watchTrack(publisher.getVideoSource?.()?.track);
                if (shouldUseBackCamera) {
                    setIsCameraRotated(true);
                    mirrorVideoElement(publisher, false);
                }
            }
        });

        publisher.on({
            accessDialogOpened: () => log.info('Publisher permission prompt opened'),
            accessDialogClosed: () => log.info('Publisher permission prompt closed'),
            accessAllowed: () => log.info('Publisher camera/microphone access allowed'),
            accessDenied: () => log.error('Publisher camera/microphone access denied'),
            // Fires as soon as the capture exists, before the init callback, so an
            // early mute is caught too.
            mediaStreamAvailable: (e: any) => e.mediaStream?.getTracks().forEach(watchTrack),
            videoElementCreated: () => log.info('Publisher video element created'),
            streamCreated: (e: any) => log.info('Publisher streamCreated:', describeStream(e.stream)),
            streamDestroyed: (e: any) => log.info('Publisher streamDestroyed:', { reason: e.reason }),
            mediaStopped: (e: any) => {
                // With audio=false the camera is the publisher's only track. iOS ends it
                // while setVideoSource() opens the other camera, the SDK sees every track
                // ended (no e.track) and would destroy the publisher, aborting the switch.
                if (!isAudioEnabled && switchingCameraRef.current && !e.track) {
                    e.preventDefault();
                    log.info('Publisher media stopped during camera switch, keeping the publisher');
                    return;
                }
                log.warn('Publisher media stopped (device lost or revoked):', describeTrack(e.track));
            },
            audioAcquisitionProblem: (e: any) => log.error('Publisher audio acquisition problem:', { method: e.method }),
            audioInputDeviceChanged: (e: any) => log.info('Publisher audio input changed:', { label: e.device?.label }),
            videoDimensionsChanged: (e: any) => log.info('Publisher video dimensions changed:', { from: e.oldValue, to: e.newValue }),
            videoDisabled: (e: any) => log.warn('Publisher video disabled:', { reason: e.reason }),
            videoEnabled: (e: any) => log.info('Publisher video enabled:', { reason: e.reason }),
            muteForced: () => log.warn('Publisher audio muted by moderator'),
            destroyed: () => log.info('Publisher destroyed'),
        });

        setSession(session);
        setPublisher(publisher);

        // Connect to the session
        const tokenValue = token.token || token; // Obsługa różnych formatów odpowiedzi
        log.info('Connecting to session with token:', tokenValue);

        session.connect(tokenValue, (error: any) => {
            if (error) {
                log.error('Error connecting to session:', error);
            } else {
                log.info('Successfully connected to session');
            }
        });
    }

    // Phones expose extra lenses (ultra-wide, telephoto, depth, ...) as separate
    // video inputs next to the two main cameras. The rotate button must only ever
    // toggle between the main front and main back camera, so these are skipped.
    const SECONDARY_LENS_PATTERN = /wide|tele|zoom|macro|depth|infrared|monochrome/i;

    const getFacingMode = (device: MediaDeviceInfo): 'user' | 'environment' | null => {
        // getCapabilities() is only available on InputDeviceInfo in some browsers.
        const capabilities = (device as { getCapabilities?: () => MediaTrackCapabilities }).getCapabilities?.();
        const reported = capabilities?.facingMode?.[0];
        if (reported === 'user' || reported === 'environment') return reported;
        if (/back|rear|environment/i.test(device.label)) return 'environment';
        if (/front|face|user/i.test(device.label)) return 'user';
        return null;
    }

    // Resolve the main camera per side WITHOUT opening a capture stream of our own:
    // calling getUserMedia while the publisher already holds the camera makes the
    // second request fail with "NotReadableError: Could not start video source".
    const resolveMainCameraIds = async () => {
        if (mainCameraIdsRef.current) return mainCameraIdsRef.current;

        const resolved: MainCameraIds = { user: null, environment: null };
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const cameras = devices.filter((device) => device.kind === 'videoinput');
            const grouped: Record<'user' | 'environment', MediaDeviceInfo[]> = { user: [], environment: [] };

            cameras.forEach((device, index) => {
                // With neither capabilities nor a usable label we can only rely on
                // enumeration order, where the front camera comes first on mobile.
                const facingMode = getFacingMode(device) ?? (index === 0 ? 'user' : 'environment');
                grouped[facingMode].push(device);
            });

            (['user', 'environment'] as const).forEach((facingMode) => {
                const group = grouped[facingMode];
                const mainCamera = group.find((device) => !SECONDARY_LENS_PATTERN.test(device.label)) ?? group[0];
                resolved[facingMode] = mainCamera?.deviceId ?? null;
            });

            log.info('Resolved main cameras:', {
                resolved,
                cameras: cameras.map((device) => ({ label: device.label, deviceId: device.deviceId, facingMode: getFacingMode(device) })),
            });
            if (resolved.user || resolved.environment) {
                mainCameraIdsRef.current = resolved;
            }
        } catch (error) {
            log.error('Error resolving camera devices:', error);
        }
        return resolved;
    }

    const mirrorVideoElement = (targetPublisher: any, mirrored: boolean) => {
        const videoElement = targetPublisher?.element?.querySelector(".OT_video-element") as HTMLElement | null;
        if (videoElement) {
            videoElement.style.transform = mirrored ? 'scaleX(-1)' : 'scaleX(1)';
        }
    }


    // Start/end of the page and browser-level events that silently kill capture
    // on mobile (backgrounding, device unplugged, permission revoked).
    useEffect(() => {
        setNrAttribute('flow', 'videoUpgradeVonage');
        setNrAttribute('vonageSessionId', qSessionId);
        setNrAttribute('accountId', qAccountId);
        setNrAttribute('interactionId', interactionId);

        log.info('Mounted:', { interactionId, sessionId: qSessionId, accountId: qAccountId, camera: cameraParam, audio: audioParam });
        logEnvironment();

        const handleVisibilityChange = () => log.info('Page visibility changed:', document.visibilityState);
        const handlePageHide = (event: PageTransitionEvent) => log.info('Page hide:', { persisted: event.persisted });
        const handleDeviceChange = () => {
            navigator.mediaDevices.enumerateDevices()
                .then((list) => log.info('Media devices changed:', list.map((device) => ({ kind: device.kind, label: device.label || '(no label)' }))))
                .catch((error) => log.error('Media devices changed, enumerate failed:', error));
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handlePageHide);
        navigator.mediaDevices?.addEventListener?.('devicechange', handleDeviceChange);

        const permissionStatuses: PermissionStatus[] = [];
        ['camera', 'microphone'].forEach((name) => {
            navigator.permissions?.query({ name } as unknown as PermissionDescriptor)
                .then((status) => {
                    status.onchange = () => log.info(`Permission ${name} changed:`, status.state);
                    permissionStatuses.push(status);
                })
                .catch(() => {
                    // Permission query for camera/microphone is not supported in every browser.
                });
        });

        const handleOtException = (event: any) => {
            log.error('OT exception:', { code: event.code, title: event.title, message: event.message });
        };
        OT.on('exception', handleOtException);

        return () => {
            log.info('Unmounted');
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', handlePageHide);
            navigator.mediaDevices?.removeEventListener?.('devicechange', handleDeviceChange);
            permissionStatuses.forEach((status) => { status.onchange = null; });
            OT.off('exception', handleOtException);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        log.info('State:', currentState);
    }, [currentState]);

    useEffect(() => {
        if (currentState === 'inCall' && tokenData && qSessionId && localVideoRef.current) {
            log.info('All conditions met, initializing Vonage...');
            initVonage(qSessionId, tokenData);
        } else if (currentState === 'inCall') {
            log.warn('inCall but not ready to initialize Vonage:', {
                hasToken: !!tokenData,
                hasSessionId: !!qSessionId,
                hasLocalVideoElement: !!localVideoRef.current,
            });
        }
    }, [currentState, tokenData, qSessionId]);

    useEffect(() => {
        const fetchToken = async () => {
            if (qSessionId && qAccountId) {
                const startedAt = Date.now();
                log.info('Fetching session token:', { sessionId: qSessionId, accountId: qAccountId });
                try {
                    const tokenResponse = await createVideoSessionToken(qSessionId, parseInt(qAccountId));
                    log.info('Token response:', { durationMs: Date.now() - startedAt, tokenResponse });
                    setTokenData(tokenResponse);
                    setCurrentState('inCall');
                } catch (error) {
                    log.error('Failed to fetch token:', { durationMs: Date.now() - startedAt }, error);
                    logger.error('Failed to fetch token', String(error));
                }
            } else {
                // Without these the page stays on the loading screen forever.
                log.error('Missing URL parameters, cannot start:', { sessionId: qSessionId, accountId: qAccountId });
            }
        };

        fetchToken();
    }, [qSessionId, qAccountId]);

    const toggleMicrophone = () => {
        const hasAudio = publisher?.stream?.hasAudio;
        log.info('Toggle microphone clicked:', { hasAudio, hasStream: !!publisher?.stream });
        if (hasAudio) {
            publisher.publishAudio(false)
            setMicrophoneMuted(true);
        } else {
            publisher.publishAudio(true)
            setMicrophoneMuted(false);

        }
    }

    const toggleCamera = () => {
        const hasVideo = publisher?.stream?.hasVideo;
        log.info('Toggle camera clicked:', { hasVideo, hasStream: !!publisher?.stream });
        if (hasVideo) {
            publisher.publishVideo(false)
            setCameraMuted(true);

        } else {
            publisher.publishVideo(true)
            setCameraMuted(false);
        }
    }

    const toggleBlur = () => {
        log.info('Toggle blur clicked:', { isVideoBlurred });
        // Both filter calls return promises; without a handler a failure would
        // only surface as an unhandled rejection.
        if (isVideoBlurred) {
            Promise.resolve(publisher.clearVideoFilter())
                .then(() => log.info('Blur cleared'))
                .catch((error: any) => log.error('Error clearing blur:', error));
            setIsVideoBlurred(false);
        } else {
            Promise.resolve(publisher.applyVideoFilter(blurSettings))
                .then(() => log.info('Blur applied'))
                .catch((error: any) => log.error('Error applying blur:', error));
            setIsVideoBlurred(true);
        }

    }

    const handleDisconnect = () => {
        log.info('Disconnect clicked:', { hasSession: !!session, hasPublisher: !!publisher });
        session?.disconnect();
        setSession(null);
        publisher?.destroy();
        setPublisher(null);
        setCurrentState('postCall');
    }

    const rotateCamera = async () => {
        if (!publisher) {
            log.warn('Rotate camera clicked without a publisher');
            return;
        }

        const targetFacingMode = isCameraRotated ? 'user' : 'environment';
        log.info('Rotate camera clicked:', { targetFacingMode, current: describePublisherSources(publisher).video });
        const { [targetFacingMode]: targetId } = await resolveMainCameraIds();
        if (!targetId) {
            log.warn(`No ${targetFacingMode}-facing camera available`);
            return;
        }

        if (publisher.getVideoSource?.()?.deviceId !== targetId) {
            log.info(`Switching to the main ${targetFacingMode}-facing camera:`, targetId);
            switchingCameraRef.current = true;
            try {
                // setVideoSource() swaps the track on the existing publisher, so it
                // never opens a second stream on the camera already in use.
                await publisher.setVideoSource(targetId);
                log.info('Video source switched:', describePublisherSources(publisher).video);
                watchTrack(publisher.getVideoSource?.()?.track);
            } catch (error) {
                log.error('Error switching video source:', error);
                return;
            } finally {
                switchingCameraRef.current = false;
            }
        } else {
            log.info(`Already on the main ${targetFacingMode}-facing camera`);
        }

        setIsCameraRotated(targetFacingMode === 'environment');
        mirrorVideoElement(publisher, targetFacingMode === 'user');
    }

    if (currentState === 'preUpgrade') return <Loading />;

    if (currentState === 'inCall')
        return (
            <>
                <InCallWrapper>
                    <Center>
                        <Content>
                            <Media>
                                <RemoteVideo id='remoteVideoInCall'>
                                    {!remoteStream && <PersonSvg />}
                                </RemoteVideo>
                                <LocalVideo ref={localVideoRef} id='localVideoInCall'>
                                    <PermissionWarning show={cameraPermissionDenied} />
                                </LocalVideo>
                                <BlurButton onClick={() => toggleBlur()} $isVideoBlurred={isVideoBlurred}>
                                    <BlurSvg />
                                </BlurButton>

                                <RotateCameraButton onClick={() => {rotateCamera();}}>
                                    <RotateCameraSvg />
                                </RotateCameraButton>
                            </Media>
                            <Controls>
                                <Left></Left>
                                <Middle>
                                    <ToggleButton $muted={microphoneMuted} onClick={() => toggleMicrophone()} style={{ cursor: 'not-allowed' }}>
                                        {microphoneMuted ? (<MicrophoneMutedSvg />) : (<MicrophoneSvg />)}
                                    </ToggleButton>
                                    <Disconnect onClick={() => handleDisconnect()}>
                                        <PhoneSvg />
                                    </Disconnect>
                                    <ToggleButton $muted={cameraMuted} onClick={() => toggleCamera()}>
                                        {cameraMuted ? (
                                            <CameraMutedSvg />
                                        ) : (
                                            <CameraSvg />
                                        )}
                                    </ToggleButton>
                                </Middle>
                                <Right $isVideoBlurred={false}>
                                    <BlurButton onClick={() => {
                                        toggleBlur();

                                    }} $isVideoBlurred={false}>
                                        <BlurSvg />
                                    </BlurButton>
                                </Right>
                            </Controls>
                            <PoweredBy>
                                <a href={poweredByLink} target="_blank">
                                    Powered by TrueEngage
                                </a>
                            </PoweredBy>
                        </Content>
                    </Center>
                </InCallWrapper >
            </>
        );

    if (currentState === 'postCall')
        return (
            <>
                <PostCall changeCurrentState={changeCurrentState} isVideoUpgrade={true} />
            </>
        );

    return null;
};

export default VideoUpgradeVonage;
